import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, LogOut, Activity, CreditCard, UserCircle, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LangToggle, useT } from "@/lib/i18n";
import { useHealth } from "@/lib/useHealth";

export default function Topbar() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const health = useHealth();
  const [sub, setSub] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [unlimited, setUnlimited] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const pull = () => api.get("/billing/me").then((r) => { setSub(r.data.subscription); setAlerts(r.data.alerts || []); setUnlimited(!!r.data.unlimited); }).catch(() => {});
    pull(); const id = setInterval(pull, 60000); return () => clearInterval(id);
  }, []);
  const daysLeft = sub ? Math.max(0, Math.ceil((new Date(sub.expires_at) - Date.now()) / 86400000)) : null;
  const renewalDue = daysLeft !== null && daysLeft <= 3;
  const cr = sub?.credits;
  const expires = sub ? new Date(sub.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : null;

  return (
    <header
      data-testid="app-topbar"
      className="h-16 sticky top-0 z-30 flex items-center gap-4 px-4 sm:px-6 bg-[#0E1019]/80 backdrop-blur-md border-b border-white/10"
    >
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs">
        <span className="font-mono text-slate-500">Workspace</span>
        <span className="font-semibold text-slate-200">Production</span>
        <ChevronDown size={14} className="text-slate-500" />
      </div>

      <div className="flex-1 max-w-md">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
          <Search size={15} className="text-slate-500" />
          <input
            data-testid="topbar-search"
            placeholder="Quick execution trigger..."
            className="bg-transparent text-sm outline-none flex-1 placeholder:text-slate-600"
          />
          <span className="text-[10px] font-mono text-slate-600 border border-white/10 rounded px-1.5 py-0.5">⌘K</span>
        </div>
      </div>

      <div data-testid="topbar-health" title={health ? `DB ${health.db ? "ok" : "down"} · media ${health.media_total} · users ${health.users}` : ""} className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${health?.status === "ok" ? "bg-emerald-500/10 border-emerald-500/20" : "bg-amber-500/10 border-amber-500/20"}`}>
        <Activity size={14} className={health?.status === "ok" ? "text-emerald-400" : "text-amber-400"} />
        <span className={`font-mono ${health?.status === "ok" ? "text-emerald-300" : "text-amber-300"}`}>{health?.renders_24h ? `${health.renders_24h.completed} renders · ${health.renders_24h.rendering} live${health.renders_24h.failed ? ` · ${health.renders_24h.failed} failed` : ""}` : health?.status === "unreachable" ? "API unreachable" : "checking…"}</span>
      </div>

      <LangToggle className="hidden md:inline-flex" />
      {cr && !unlimited && (
        <Link to="/account" data-testid="credit-meter" title={`${sub.pack_name} pack · expires ${expires}`} className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-[11px] font-mono text-slate-300">
          <span data-testid="credit-meter-video" className={cr.video_credits_left <= 0.5 ? "text-red-300" : "text-purple-200"}>{cr.video_credits_left.toLocaleString("en-IN")} video credits</span><span className="text-slate-600">·</span>
          <span data-testid="credit-meter-voice">{cr.voice_min_left.toLocaleString("en-IN")} voice min</span><span className="text-slate-600">·</span>
          <span data-testid="credit-meter-images">{cr.images_left.toLocaleString("en-IN")} images</span><span className="text-slate-600">·</span>
          <span data-testid="credit-meter-expiry" className={renewalDue ? "text-amber-300" : "text-slate-400"}>expires {expires}</span>
        </Link>
      )}
      {alerts.length > 0 && !dismissed && (
        <div data-testid="billing-alert" className="fixed top-16 right-4 z-40 max-w-sm rounded-xl border border-amber-500/30 bg-[#161222]/95 backdrop-blur px-4 py-3 shadow-xl">
          <div className="flex items-start gap-2">
            <Bell size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1">{alerts.map((a, i) => <p key={i} data-testid={`billing-alert-${a.type}`} className="text-xs text-amber-100">{a.message}</p>)}<Link to="/billing" className="text-[11px] text-cyan-300 underline">Top up / renew</Link></div>
            <button data-testid="billing-alert-dismiss" onClick={() => setDismissed(true)} className="text-slate-500 hover:text-white text-xs">✕</button>
          </div>
        </div>
      )}
      <Link to="/billing" data-testid="topbar-pack-chip" className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors ${sub && !renewalDue ? "bg-purple-500/10 border-purple-500/25 text-purple-200 hover:bg-purple-500/20" : "bg-amber-500/10 border-amber-500/25 text-amber-200 hover:bg-amber-500/20"}`}>
        <CreditCard size={14} />
        <span className="font-mono">{sub ? (renewalDue ? `${sub.pack_name} · renews in ${daysLeft}d` : `${sub.pack_name} pack`) : t("Get a pack")}</span>
      </Link>

      <div className="relative">
        <button
          data-testid="user-menu-button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full bg-white/5 border border-white/10 hover:border-purple-500/40 transition-colors"
        >
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500" />
          )}
          <div className="hidden sm:block text-left">
            <div className="text-xs font-semibold leading-none">{user?.name?.split(" ")[0]}</div>
            <div className="text-[10px] font-mono uppercase text-purple-400">{user?.role}</div>
          </div>
          <ChevronDown size={14} className="text-slate-500" />
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-56 glass rounded-xl p-2 z-50">
            <div className="px-3 py-2 border-b border-white/10 mb-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <div className="mt-1.5 inline-block text-[10px] font-mono uppercase px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300">
                {user?.role}
              </div>
            </div>
            <Link to="/account" data-testid="menu-my-account" onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 transition-colors">
              <UserCircle size={15} /> My Account
            </Link>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
